import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useSuppliers } from '../../graphql/queries';
import { deleteSupplier, ApiError } from '../../lib/apiClient';
import { usePermission } from '../../hooks/usePermission';
import { LoadingSpinner, EmptyState, isDeltaEmpty } from '../../components/shared';
import type { TechnologySupplier } from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/suppliers/')({
  component: SuppliersPage,
});

export { SuppliersPage };

function extractDescriptionText(value: string | null | undefined): string {
  if (!value || isDeltaEmpty(value)) return '';
  try {
    const delta = JSON.parse(value) as { ops?: Array<{ insert?: unknown }> };
    return (
      delta.ops
        ?.map((op) => (typeof op.insert === 'string' ? op.insert : ''))
        .join('')
        .trim() ?? ''
    );
  } catch {
    return value.trim();
  }
}

function SuppliersPage(): JSX.Element {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const canManage = usePermission('technology:create');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, loading, error } = useSuppliers();
  const suppliers = [...(data?.suppliers ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const CardElement = 'forge-card' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeIcon = 'forge-icon' as React.ElementType;

  const handleEdit = (supplier: TechnologySupplier): void => {
    void navigate({
      to: '/suppliers/$supplierId/edit',
      params: { supplierId: supplier.entityId },
    });
  };

  const handleDelete = async (
    e: React.MouseEvent,
    supplierId: string,
    name: string
  ): Promise<void> => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteSupplier(supplierId);
      await apolloClient.refetchQueries({ include: ['ListSuppliers'] });
      setDeleteError(null);
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete supplier');
    }
  };

  if (loading) return <LoadingSpinner centered message="Loading suppliers..." />;

  if (error) {
    return <EmptyState icon="error" title="Failed to load suppliers" description={error.message} />;
  }

  return (
    <div className="suppliers-page">
      <div className="page-header page-header--toolbar page-header--center page-header--spaced">
        <h1 className="forge-typography--headline4">Suppliers</h1>
        {canManage && (
          <Link to="/suppliers/new">
            <ForgeButton variant="raised">
              <ForgeIcon name="add" slot="start" />
              Add Supplier
            </ForgeButton>
          </Link>
        )}
      </div>

      {deleteError && (
        <p role="alert" className="suppliers-page__error">
          {deleteError}
        </p>
      )}

      {suppliers.length === 0 ? (
        <EmptyState
          icon="business"
          title="No suppliers"
          description="Add a supplier to track technology vendors and their products."
          actionLabel={canManage ? 'Add Supplier' : undefined}
          onAction={canManage ? () => void navigate({ to: '/suppliers/new' }) : undefined}
        />
      ) : (
        <div className="suppliers-page__grid">
          {suppliers.map((supplier) => (
            <CardElement key={supplier.entityId} no-padding>
              <div className="supplier-card__title-bar">
                <p className="supplier-card__title forge-typography--subtitle1">{supplier.name}</p>
              </div>
              <div className="supplier-card__content">
                {extractDescriptionText(supplier.description) && (
                  <p className="supplier-card__description forge-typography--body2">
                    {extractDescriptionText(supplier.description)}
                  </p>
                )}
                {supplier.websiteUrl && (
                  <a
                    href={supplier.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="supplier-card__website forge-typography--body2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ForgeIcon name="open_in_new" className="supplier-card__website-icon" />
                    {supplier.websiteUrl}
                  </a>
                )}
                {supplier.technologyProductCount == null ? (
                  <span className="supplier-card__tech-count supplier-card__tech-count--unavailable forge-typography--body2">
                    <ForgeIcon name="memory" className="supplier-card__tech-count-icon" />
                    count unavailable
                  </span>
                ) : (
                  <Link
                    to="/technologies"
                    search={{
                      page: 1,
                      pageSize: 25,
                      supplierId: supplier.entityId,
                      productFamilyId: '',
                    }}
                    className="supplier-card__tech-count forge-typography--body2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ForgeIcon name="memory" className="supplier-card__tech-count-icon" />
                    {supplier.technologyProductCount}{' '}
                    {supplier.technologyProductCount === 1
                      ? 'technology product'
                      : 'technology products'}
                  </Link>
                )}
              </div>
              {canManage && (
                <div className="supplier-card__action-bar">
                  <ForgeButton
                    variant="outlined"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleEdit(supplier);
                    }}
                  >
                    Edit
                  </ForgeButton>
                  <ForgeButton
                    variant="outlined"
                    onClick={(e: React.MouseEvent) =>
                      void handleDelete(e, supplier.entityId, supplier.name)
                    }
                  >
                    Delete
                  </ForgeButton>
                </div>
              )}
            </CardElement>
          ))}
        </div>
      )}
    </div>
  );
}
